import { useEffect, Suspense, lazy } from 'react'
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthProvider'
import Layout from '@/components/Layout'
import { ErrorBoundary } from '@/components/ErrorBoundary'

// Lazy load app routes for code splitting
const Login = lazy(() => import('@/pages/Login'))
const Registro = lazy(() => import('@/pages/Registro'))
const ResetPassword = lazy(() => import('@/pages/ResetPassword'))
const Landing = lazy(() => import('@/pages/Landing'))
const Agenda = lazy(() => import('@/pages/Agenda'))
const Clientes = lazy(() => import('@/pages/Clientes'))
const Recordatorios = lazy(() => import('@/pages/Recordatorios'))
const Resumen = lazy(() => import('@/pages/Resumen'))
const Ajustes = lazy(() => import('@/pages/Ajustes'))
const Inventario = lazy(() => import('@/pages/Inventario'))
const SobreNosotros = lazy(() => import('@/pages/SobreNosotros'))
const Contacto = lazy(() => import('@/pages/Contacto'))
const Prensa = lazy(() => import('@/pages/Prensa'))
const Privacidad = lazy(() => import('@/pages/Privacidad'))
const Terminos = lazy(() => import('@/pages/Terminos'))
const Cookies = lazy(() => import('@/pages/Cookies'))

// Callback público: restaura sesión desde hash fragment (redirect desde login en dominio principal)
function AuthCallback() {
  const { session, cargando } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!cargando && session) {
      navigate('/app/agenda', { replace: true })
    }
  }, [cargando, session, navigate])

  return <p className="p-6 text-center text-muted-foreground">Restaurando sesión…</p>
}

function RutaProtegida() {
  const { session, tenantId, cargando } = useAuth()
  if (cargando) return <p className="p-6 text-muted-foreground">Cargando...</p>
  if (!session) return <Navigate to="/login" replace />
  if (!tenantId) return <Navigate to="/registro" replace />
  return (
    <Layout>
      <Suspense fallback={<p className="p-6 text-center text-muted-foreground">Cargando…</p>}>
        <Outlet />
      </Suspense>
    </Layout>
  )
}

function RutaAdmin() {
  const { esAdmin } = useAuth()
  return esAdmin ? (
    <Suspense fallback={<p className="p-6 text-center text-muted-foreground">Cargando…</p>}>
      <Outlet />
    </Suspense>
  ) : (
    <Navigate to="/app" replace />
  )
}

// Componente que decide qué mostrar en "/" según el dominio
function RootRoute() {
  const { session, tenantId, cargando } = useAuth()

  if (cargando) return <p className="p-6 text-muted-foreground">Cargando...</p>

  // Detectar dominio principal vs subdominio por hostname
  const hostname = window.location.hostname
  const esDominioPrincipal = hostname === 'beautyhub.com.ar' || hostname === 'www.beautyhub.com.ar'

  if (esDominioPrincipal) {
    // Dominio principal: siempre landing (incluso con sesión)
    return <Landing />
  }

  // Subdominio: si hay tenantId → app, si no → login
  if (tenantId) {
    if (!session) return <Navigate to="/login" replace />
    return <Navigate to="/app/agenda" replace />
  }

  // Subdominio sin tenantId (raro) → login
  return <Navigate to="/login" replace />
}

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<p className="p-6 text-center text-muted-foreground">Cargando…</p>}>
        <Routes>
        {/* Raíz inteligente: landing en dominio principal, app en subdominios */}
        <Route path="/" element={<RootRoute />} />

        {/* Callback público: restaura sesión desde hash (redirect desde login dominio principal) */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Auth */}
        <Route path="/login" element={<Login />} />
        <Route path="/registro" element={<Registro />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Landing footer pages (públicas) */}
        <Route path="/sobre-nosotros" element={<SobreNosotros />} />
        <Route path="/contacto" element={<Contacto />} />
        <Route path="/prensa" element={<Prensa />} />
        <Route path="/privacidad" element={<Privacidad />} />
        <Route path="/terminos" element={<Terminos />} />
        <Route path="/cookies" element={<Cookies />} />

        {/* App protegida */}
        <Route path="/app" element={<RutaProtegida />}>
          <Route index element={<Navigate to="/app/agenda" replace />} />
          <Route path="agenda" element={<Agenda />} />
          <Route path="clientes" element={<Clientes />} />
          <Route path="recordatorios" element={<Recordatorios />} />
          <Route element={<RutaAdmin />}>
            <Route path="resumen" element={<Resumen />} />
            <Route path="ajustes" element={<Ajustes />} />
            <Route path="inventario" element={<Inventario />} />
          </Route>
        </Route>

        {/* Fallback: redirigir a raíz */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  </ErrorBoundary>
  )
}
